import React, { useEffect, useRef, useState } from 'react';
import { loginWithGoogle } from '../../services/api';

const GOOGLE_SCRIPT_ID = 'google-identity-services';
const GOOGLE_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

const loadGoogleScript = () => new Promise((resolve, reject) => {
  if (window.google?.accounts?.id) {
    resolve();
    return;
  }
  const existing = document.getElementById(GOOGLE_SCRIPT_ID);
  if (existing) {
    existing.addEventListener('load', resolve, { once: true });
    existing.addEventListener('error', reject, { once: true });
    return;
  }
  const script = document.createElement('script');
  script.id = GOOGLE_SCRIPT_ID;
  script.src = GOOGLE_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  script.onload = resolve;
  script.onerror = reject;
  document.body.appendChild(script);
});

// The Google Identity button + credential exchange, extracted so it can be
// reused by both the full-page gate and the login modal.
const GoogleSignInButton = ({ onLogin, width = 280 }) => {
  const buttonRef = useRef(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // .trim(): a stray trailing newline in the env var (e.g. pasted from a
  // file/CI secret) silently becomes part of this string and gets URL-encoded
  // into the Google request as %0D%0A, which Google rejects as invalid_client
  // even though the visible ID looks correct everywhere it's inspected.
  const clientId = (process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim() || undefined;

  useEffect(() => {
    if (!clientId || !buttonRef.current) return;
    let mounted = true;
    loadGoogleScript()
      .then(() => {
        if (!mounted || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: async (response) => {
            if (!response?.credential) {
              setError('Google did not return a sign-in credential.');
              return;
            }
            setLoading(true);
            setError('');
            try {
              const result = await loginWithGoogle(response.credential);
              onLogin(result);
            } catch (err) {
              setError('Google sign-in failed. Please try again.');
            } finally {
              setLoading(false);
            }
          },
        });
        window.google.accounts.id.renderButton(buttonRef.current, {
          theme: 'outline',
          size: 'large',
          shape: 'rectangular',
          text: 'signin_with',
          width,
        });
      })
      .catch(() => {
        if (mounted) setError('Could not load Google sign-in.');
      });
    return () => { mounted = false; };
  }, [clientId, onLogin, width]);

  return (
    <div className="w-full">
      <div className="flex min-h-[44px] items-center justify-center">
        {clientId ? (
          <div ref={buttonRef} />
        ) : (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Google client ID is not configured.
          </div>
        )}
      </div>
      {loading && <p className="mt-3 text-center text-sm text-gray-500">Signing in...</p>}
      {error && <p className="mt-3 text-center text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default GoogleSignInButton;
